import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import testIds from "constants/testIds";
import { useRequestFormReviewValidation } from "@RequestFormV2/context/reviewValidation";
import type { PackingListRow } from "@RequestFormV2/sections/PackingList/types";
import { canDeletePackingListRow } from "@RequestFormV2/sections/PackingList/utils/packingListRowActions";
import { hasParcelHandlingUnit } from "@RequestFormV2/sections/PackingList/utils/parcelRequirements";
import { usePackingList } from "@RequestFormV2/sections/PackingList/context";
import AddRowButton from "@RequestFormV2/sections/PackingList/components/shared/AddRowButton";
import PackingListTable from "@RequestFormV2/sections/PackingList/components/shared/PackingListTable";
import ParcelWarningBadge from "@RequestFormV2/sections/PackingList/components/shared/ParcelWarningBadge";
import HandlingUnitTableRow from "@RequestFormV2/sections/PackingList/components/HandlingUnitsSection/HandlingUnitTableRow";
import { HANDLING_UNIT_COLUMN_WIDTHS } from "@RequestFormV2/sections/PackingList/components/shared/packingListColumnWidths";
import { useStopSelectOptions } from "@RequestFormV2/sections/PackingList/utils/stopSelectOptions";
import PackingListWorkflowTableLoading from "@RequestFormV2/sections/PackingList/components/shared/PackingListWorkflowTableLoading";
import {
  getPackingListWorkflowColumnFlags,
  isPackingListWorkflowConfigReady,
} from "@RequestFormV2/sections/PackingList/utils/packingListWorkflowFlags";

export default function HandlingUnitsSection() {
  const intl = useIntl();
  const packingList = useWatch({ name: "packing_list" }) as PackingListRow[] | undefined;
  const { location, packingListConfig, isLoadingPackingListConfig, appendHandlingUnit, isParcelFreight, handlingUnitRows } =
    usePackingList();
  const { hasAttemptedReview } = useRequestFormReviewValidation();
  const { isMultiStop } = useStopSelectOptions();

  const isWorkflowConfigReady = isPackingListWorkflowConfigReady(location, isLoadingPackingListConfig);
  const { cratingEnabled, hazardousEnabled, temperatureEnabled, dangerousEnabled, nmfcEnabled } =
    getPackingListWorkflowColumnFlags(packingListConfig);
  const hasHandlingUnit = hasParcelHandlingUnit(handlingUnitRows.map(({ row }) => row));
  const showParcelWarning = isParcelFreight && !hasHandlingUnit;

  const columns = useMemo(
    () => [
      {
        key: "handling_unit",
        header: intl.formatMessage({ id: "request-form.packing-list.handlingUnit", defaultMessage: "Handling unit" }),
        width: HANDLING_UNIT_COLUMN_WIDTHS.handlingUnit,
      },
      {
        key: "dimension",
        header: intl.formatMessage({ id: "request-form.packing-list.dimension", defaultMessage: "Dimension" }),
        width: HANDLING_UNIT_COLUMN_WIDTHS.dimension * 3 + HANDLING_UNIT_COLUMN_WIDTHS.dimensionUnit,
      },
      {
        key: "weight",
        header: intl.formatMessage({ id: "request-form.packing-list.weight", defaultMessage: "Weight" }),
        width: HANDLING_UNIT_COLUMN_WIDTHS.weight + HANDLING_UNIT_COLUMN_WIDTHS.weightUnit,
      },
      {
        key: "qty",
        header: intl.formatMessage({ id: "request-form.packing-list.qty", defaultMessage: "Qty" }),
        width: HANDLING_UNIT_COLUMN_WIDTHS.qty,
      },
      ...(nmfcEnabled
        ? [
            {
              key: "nmfc",
              header: intl.formatMessage({ id: "request-form.packing-list.nmfc", defaultMessage: "NMFC" }),
              width: HANDLING_UNIT_COLUMN_WIDTHS.nmfc,
            },
          ]
        : []),
      ...(isMultiStop
        ? [
            {
              key: "pickup_stop",
              header: intl.formatMessage({ id: "general.pickup", defaultMessage: "Pickup" }),
              width: HANDLING_UNIT_COLUMN_WIDTHS.stop,
            },
            {
              key: "delivery_stop",
              header: intl.formatMessage({ id: "general.delivery", defaultMessage: "Delivery" }),
              width: HANDLING_UNIT_COLUMN_WIDTHS.stop,
            },
          ]
        : []),
      {
        key: "stackable",
        header: intl.formatMessage({ id: "request-form.packing-list.stackable", defaultMessage: "Stackable" }),
        width: HANDLING_UNIT_COLUMN_WIDTHS.toggle,
        className: "rfv2-packing-list-table__header-cell--center",
      },
      ...(hazardousEnabled
        ? [
            {
              key: "hazardous",
              header: intl.formatMessage({ id: "request-form.packing-list.hazardous", defaultMessage: "Hazardous" }),
              width: HANDLING_UNIT_COLUMN_WIDTHS.toggle,
              className: "rfv2-packing-list-table__header-cell--center",
            },
          ]
        : []),
      ...(cratingEnabled
        ? [
            {
              key: "crating",
              header: intl.formatMessage({ id: "request-form.packing-list.crating", defaultMessage: "Crating" }),
              width: HANDLING_UNIT_COLUMN_WIDTHS.crating,
            },
          ]
        : []),
      ...(temperatureEnabled
        ? [
            {
              key: "temperature",
              header: intl.formatMessage({
                id: "request-form.packing-list.temperatureControl",
                defaultMessage: "Temperature control",
              }),
              width: HANDLING_UNIT_COLUMN_WIDTHS.temperature,
            },
          ]
        : []),
      ...(dangerousEnabled
        ? [
            {
              key: "dangerous",
              header: intl.formatMessage({
                id: "request-form.packing-list.dangerousGoods",
                defaultMessage: "Dangerous goods",
              }),
              width: HANDLING_UNIT_COLUMN_WIDTHS.dangerous,
            },
            {
              key: "un_number",
              header: intl.formatMessage({
                id: "request-form.packing-list.unNumber",
                defaultMessage: "UN Number",
              }),
              width: HANDLING_UNIT_COLUMN_WIDTHS.unNumber,
            },
          ]
        : []),
      {
        key: "additional",
        header: intl.formatMessage({
          id: "request-form.packing-list.additionalInformation",
          defaultMessage: "Additional information",
        }),
        width: HANDLING_UNIT_COLUMN_WIDTHS.additional,
      },
    ],
    [intl, cratingEnabled, hazardousEnabled, temperatureEnabled, dangerousEnabled, nmfcEnabled, isMultiStop],
  );

  return (
    <section className="rfv2-packing-list__subsection">
      <div className="rfv2-packing-list__subsection-title">
        {intl.formatMessage({
          id: "request-form.packing-list.handlingUnits",
          defaultMessage: "Handling units",
        })}
        {showParcelWarning && <ParcelWarningBadge color={hasAttemptedReview ? "error" : "warning"} />}
      </div>

      {isWorkflowConfigReady ? (
        <PackingListTable columns={columns} scrollable testId={testIds.REQUEST_FORM_PACKING_LIST_HANDLING_UNIT_TABLE}>
          {handlingUnitRows.map(({ index, fieldId, row }, displayIndex) => (
            <HandlingUnitTableRow
              key={fieldId}
              index={index}
              fieldId={fieldId}
              rowNumber={displayIndex + 1}
              fallbackRow={row}
              deleteDisabled={!canDeletePackingListRow(packingList ?? [], index)}
              isMultiStop={isMultiStop}
            />
          ))}
        </PackingListTable>
      ) : (
        <PackingListWorkflowTableLoading />
      )}

      <AddRowButton
        label={intl.formatMessage({
          id: "request-form.packing-list.addHandlingUnit",
          defaultMessage: "Add handling unit",
        })}
        onClick={appendHandlingUnit}
        testId={testIds.REQUEST_FORM_PACKING_LIST_ADD_HANDLING_UNIT_BUTTON}
      />
    </section>
  );
}
